import * as React from 'react'
import { Modal, Button } from 'antd'
import {
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'

/**
 * useConfirm - 声明式确认对话框 Hook
 *
 * 解决 Semi UI Modal.confirm() 在 React 19 中的兼容性问题
 * （imperative API 会导致 "Attempted to synchronously unmount a root" 错误）
 *
 * 使用示例：
 * ```tsx
 * const { confirm, ConfirmDialog } = useConfirm()
 *
 * const handleDelete = () => {
 *   confirm({
 *     title: '确认删除',
 *     content: '确定要删除吗？',
 *     type: 'danger',
 *     onOk: async () => {
 *       await deleteItem()
 *     }
 *   })
 * }
 *
 * return (
 *   <>
 *     <Button onClick={handleDelete}>删除</Button>
 *     <ConfirmDialog />
 *   </>
 * )
 * ```
 */

export interface ConfirmOptions {
  /** 标题 */
  title: string
  /** 内容 */
  content?: React.ReactNode
  /** 确认回调 */
  onOk?: () => void | Promise<void>
  /** 取消回调 */
  onCancel?: () => void
  /** 确认按钮文字 */
  okText?: string
  /** 取消按钮文字 */
  cancelText?: string
  /** 类型：影响图标和按钮颜色 */
  type?: 'warning' | 'danger' | 'success' | 'info'
  /** 是否显示图标 */
  showIcon?: boolean
}

interface ConfirmState extends ConfirmOptions {
  open: boolean
  loading: boolean
}

const initialState: ConfirmState = {
  open: false,
  loading: false,
  title: '',
  content: undefined,
  onOk: undefined,
  onCancel: undefined,
  okText: '确定',
  cancelText: '取消',
  type: 'warning',
  showIcon: true,
}

export function useConfirm() {
  const [state, setState] = React.useState<ConfirmState>(initialState)

  const confirm = React.useCallback((options: ConfirmOptions) => {
    setState({
      ...initialState,
      ...options,
      open: true,
      loading: false,
    })
  }, [])

  const handleClose = React.useCallback(() => {
    setState((prev) => ({ ...prev, open: false }))
    state.onCancel?.()
  }, [state.onCancel])

  const handleOk = React.useCallback(async () => {
    if (state.onOk) {
      setState((prev) => ({ ...prev, loading: true }))
      try {
        await state.onOk()
        setState((prev) => ({ ...prev, open: false, loading: false }))
      } catch (error) {
        setState((prev) => ({ ...prev, loading: false }))
        throw error
      }
    } else {
      setState((prev) => ({ ...prev, open: false }))
    }
  }, [state.onOk])

  // 图标映射
  const IconComponent = React.useMemo(() => {
    const iconStyle = { fontSize: '24px' }
    switch (state.type) {
      case 'danger':
        return <CloseCircleOutlined className="text-red-500" style={iconStyle} />
      case 'success':
        return <CheckCircleOutlined className="text-green-500" style={iconStyle} />
      case 'info':
        return <InfoCircleOutlined className="text-blue-500" style={iconStyle} />
      case 'warning':
      default:
        return <ExclamationCircleOutlined className="text-amber-500" style={iconStyle} />
    }
  }, [state.type])

  // 确认按钮类型
  const okButtonType = state.type === 'danger' ? 'danger' : 'primary'

  const ConfirmDialog = React.useCallback(
    () => (
      <Modal
        open={state.open}
        onCancel={handleClose}
        closable={false}
        maskClosable={false}
        centered
        width={420}
        title={null}
        footer={
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={handleClose} disabled={state.loading}>
              {state.cancelText}
            </Button>
            <Button
              type="primary"
              danger={state.type === 'danger'}
              onClick={handleOk}
              loading={state.loading}
            >
              {state.okText}
            </Button>
          </div>
        }
      >
        <div className="flex gap-4 py-2">
          {state.showIcon && (
            <div className="flex-shrink-0 pt-0.5">{IconComponent}</div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              {state.title}
            </h3>
            {state.content && (
              <div className="text-sm text-gray-600">{state.content}</div>
            )}
          </div>
        </div>
      </Modal>
    ),
    [state, handleClose, handleOk, IconComponent, okButtonType]
  )

  return {
    confirm,
    ConfirmDialog,
  }
}

/**
 * ConfirmProvider - 全局确认对话框 Provider
 *
 * 用于在应用根部提供全局的确认对话框功能
 *
 * 使用示例：
 * ```tsx
 * // 在 layout.tsx 中
 * <ConfirmProvider>
 *   {children}
 * </ConfirmProvider>
 *
 * // 在任意组件中
 * const { confirm } = useConfirmContext()
 * confirm({ title: '确认', content: '确定吗？' })
 * ```
 */

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => void
}

const ConfirmContext = React.createContext<ConfirmContextValue | null>(null)

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { confirm, ConfirmDialog } = useConfirm()

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmDialog />
    </ConfirmContext.Provider>
  )
}

export function useConfirmContext() {
  const context = React.useContext(ConfirmContext)
  if (!context) {
    throw new Error('useConfirmContext must be used within a ConfirmProvider')
  }
  return context
}
